import React, { FC, useEffect, useState } from 'react';

import { Spinner } from '@rbilabs/universal-components';

import { IServerOrder } from '@rbi-ctg/menu';
import { PaymentProcessLoading } from 'components/payment-process-loading';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { OrderStatus } from 'state/order/types';
import { ServiceMode } from 'state/service-mode';

import ConfirmPickup from './confirm-pickup';
import DeliveryConfirmation from './delivery/delivery-confirmation';
import PickupConfirmed from './pickup-confirmed';

interface IOrderConfirmationContentProps {
  loading: boolean;
  serverOrder: IServerOrder | null;
}

export const OrderConfirmationPageContent: FC<React.PropsWithChildren<
  IOrderConfirmationContentProps
>> = (props: IOrderConfirmationContentProps) => {
  const { loading, serverOrder } = props;
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);
  const [needConfirmation, setNeedConfirmation] = useState<boolean | undefined>(true);
  const enableCheckoutAnimation = useFlag(LaunchDarklyFlag.ENABLE_CHECKOUT_ANIMATION);

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const showImHereFlow = orderInjectFlow === OrderInjectionFlowVariations.IM_HERE;

  useEffect(() => {
    if (serverOrder?.cart.serviceMode && needConfirmation === undefined) {
      setNeedConfirmation(showImHereFlow);
    }
  }, [showImHereFlow, needConfirmation, serverOrder?.cart.serviceMode]);

  if (!serverOrder) {
    return <Spinner marginTop={'50%'} />;
  }

  const {
    cart: { serviceMode },
    status: orderStatus,
  } = serverOrder;

  if (
    loading ||
    (enableCheckoutAnimation && orderStatus === OrderStatus.PRICE_SUCCESSFUL && showImHereFlow)
  ) {
    return (
      <ConfirmPickup
        serverOrder={serverOrder}
        needConfirm={true}
        setNeedConfirmation={setNeedConfirmation}
        setProcessingPayment={setProcessingPayment}
      />
    );
  }

  if (enableCheckoutAnimation && processingPayment) {
    return (
      <PaymentProcessLoading
        rbiOrderId={serverOrder?.rbiOrderId}
        onFinished={() => {
          setProcessingPayment(false);
        }}
      />
    );
  }

  if (serviceMode === ServiceMode.DELIVERY) {
    return <DeliveryConfirmation serverOrder={serverOrder} />;
  }

  if (loading) {
    <Spinner marginTop={'50%'} />;
  }

  switch (orderStatus) {
    case OrderStatus.INSERT_REQUESTED:
    case OrderStatus.PAYMENT_REQUESTED:
    case OrderStatus.PAYMENT_REQUIRED:
    case OrderStatus.PAYMENT_SUCCESSFUL:
    case OrderStatus.PRICE_SUCCESSFUL:
    case OrderStatus.VALIDATION_ERROR:
      return (
        <ConfirmPickup
          serverOrder={serverOrder}
          setNeedConfirmation={setNeedConfirmation}
          needConfirm={!!needConfirmation}
          setProcessingPayment={setProcessingPayment}
        />
      );
    case OrderStatus.INSERT_SUCCESSFUL:
    case OrderStatus.UPDATE_REQUESTED:
    case OrderStatus.UPDATE_ERROR:
    case OrderStatus.UPDATE_SUCCESSFUL:
      return <PickupConfirmed serverOrder={serverOrder} />;
    default:
      return <Spinner marginTop={'50%'} />;
  }
};
